<script setup lang="ts">
import { computed } from 'vue';

export type FieldPriority =
  | 'primary'
  | 'info'
  | 'danger'
  | 'success'
  | 'warning';

export interface Props {
  priority?: FieldPriority;
  isLabelFirst?: boolean;
  isLabelColor?: boolean;
  inset?: boolean;
  label?: string;
  disabled?: boolean;
  isSmall?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  priority: 'primary',
  isLabelFirst: false,
  isLabelColor: false,
  inset: false,
  label: undefined,
  disabled: false,
  isSmall: false,
});

const model = defineModel<boolean>({
  default: undefined,
});

const varColorClass = computed(() => 'var-color-' + props.priority);
const colorClass = computed(() => 'color-' + props.priority);
</script>

<template>
  <v-switch
    v-model="model"
    :class="[
      $style.kSwitchField,
      $style[colorClass],
      { [$style.isLabelFirst]: props.isLabelFirst },
      { [$style.isInset]: props.inset },
      { [$style.isDisabled]: props.disabled },
      { [$style.isSmall]: props.isSmall },
    ]"
    data-testid="kSwitchField"
    :color="$style[varColorClass]"
    density="compact"
    :inset="props.inset"
    :label="props.label"
    :disabled="props.disabled"
  >
    <template v-if="props.label" #label="{ label: _label }">
      <span
        :style="{
          color: isLabelColor && model ? $style[varColorClass] : '',
        }"
      >
        {{ _label }}
      </span>
    </template>
  </v-switch>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

:export {
  var-color-primary: colors.$color-brand-800;
  var-color-success: colors.$color-green-600;
  var-color-danger: colors.$color-red-600;
  var-color-warning: colors.$color-yellow-500;
  var-color-info: colors.$color-brand-400;
}

.kSwitchField {
  :global {
    .v-input__control {
    }
      .v-selection-control {
        gap: 10px;
      }
        .v-selection-control__wrapper {
        }
          .v-switch__track {
            background-color: colors.$color-text-tertiary;
          }
        .v-label {
          padding: 0 !important;
          opacity: 1;
          color: colors.$color-text-secondary;
        }
      .v-selection-control--disabled {
        pointer-events: auto;

        .v-selection-control__wrapper {
        }
          .v-switch__track {
            cursor: not-allowed;
          }
          .v-selection-control__input input {
            cursor: not-allowed;
          }

        .v-label--clickable {
          cursor: not-allowed;
        }
      }
    .v-input__details {
    }
  }
}

.kSwitchField.color-danger {
  :global {
    .v-switch__track {
      background-color: colors.$color-red-600 !important;
    }
  }
}

.kSwitchField.color-success {
  :global {
    .v-switch__track {
      background-color: colors.$color-green-600 !important;
    }
  }
}

.kSwitchField.isInset {
  :global {
    .v-switch__track {
      opacity: 1;
    }
  }
}

.kSwitchField.isLabelFirst {
  :global {
    .v-input__control {
    }
      .v-selection-control {
      }
        .v-selection-control__wrapper {
          order: 2;
        }
        .v-label {
          order: 1;
        }
    .v-input__details {
      text-align: right;
    }
  }
}

.kSwitchField.isSmall {
  --v-input-control-height: 34px;
  :global {
    .v-selection-control {
      gap: 8px;
    }
    .v-switch__track {
      height: 20px;
      min-width: 40px;
    }
    .v-switch__thumb {
      width: 16px;
      height: 16px;
    }
  }
}
</style>
